/**
 * Shared helpers for workflow-linked dynamic forms (builder + task/mock runtimes).
 * Keeps field shape, instance merge, validation, and conditional match options in one place.
 */

import {
  normalizeConditionalBehavior,
  formConditionEvaluators,
} from "@/lib/formFieldConditions"
import {
  normalizeDateOffset,
  validateDateFieldRange,
  getDateFieldRangeErrors,
} from "@/lib/formDateFieldConfig"

export { validateDateFieldRange, getDateFieldRangeErrors }

const FALLBACK_COMPLETION_FORM_FIELDS_RAW = [
  {
    id: "completion_notes",
    label: "Completion Notes",
    type: "textarea",
    required: true,
  },
  {
    id: "completion_date",
    label: "Completion Date",
    type: "date",
    required: true,
  },
  {
    id: "completion_method",
    label: "Completion Method",
    type: "select",
    required: true,
    options: [
      { value: "manual", label: "Manual" },
      { value: "automated", label: "Automated" },
      { value: "external", label: "External System" },
    ],
  },
]

/**
 * Normalize validation object, preserving number + date constraint keys.
 * @param {unknown} validation
 */
function normalizeFieldValidation(validation) {
  if (!validation || typeof validation !== "object") return {}
  const next = { ...validation }
  if (next.minDate != null) next.minDate = String(next.minDate)
  if (next.maxDate != null) next.maxDate = String(next.maxDate)
  const minOff = normalizeDateOffset(next.minDateOffset)
  const maxOff = normalizeDateOffset(next.maxDateOffset)
  if (minOff) next.minDateOffset = minOff
  else delete next.minDateOffset
  if (maxOff) next.maxDateOffset = maxOff
  else delete next.maxDateOffset
  return next
}

/**
 * Prefer snapshot (resolved) minDate/maxDate over template expressions.
 * @param {Record<string, unknown>} templateValidation
 * @param {Record<string, unknown>} snapshotValidation
 */
function mergeFieldValidation(templateValidation, snapshotValidation) {
  const t = normalizeFieldValidation(templateValidation)
  const s = normalizeFieldValidation(snapshotValidation)
  const merged = { ...t, ...s }
  const sMin = s.minDate != null ? String(s.minDate).trim() : ""
  const sMax = s.maxDate != null ? String(s.maxDate).trim() : ""
  const tMin = t.minDate != null ? String(t.minDate).trim() : ""
  const tMax = t.maxDate != null ? String(t.maxDate).trim() : ""
  if (sMin !== "") merged.minDate = sMin
  else if (tMin !== "") merged.minDate = tMin
  else delete merged.minDate
  if (sMax !== "") merged.maxDate = sMax
  else if (tMax !== "") merged.maxDate = tMax
  else delete merged.maxDate
  // Snapshot after resolve clears offsets; keep template offsets only when snapshot omitted them
  if (s.minDateOffset) merged.minDateOffset = s.minDateOffset
  else if (t.minDateOffset && sMin === "") merged.minDateOffset = t.minDateOffset
  else delete merged.minDateOffset
  if (s.maxDateOffset) merged.maxDateOffset = s.maxDateOffset
  else if (t.maxDateOffset && sMax === "") merged.maxDateOffset = t.maxDateOffset
  else delete merged.maxDateOffset
  return merged
}

/**
 * Canonical runtime field (normalized conditional rules).
 * @param {Record<string, unknown>} field
 */
export function normalizeFormField(field) {
  if (!field || typeof field !== "object" || field.id == null) {
    return null
  }
  const defaultValueOffset = normalizeDateOffset(field.defaultValueOffset)
  const normalized = {
    id: field.id,
    label: field.label ?? "",
    type: field.type ?? "text",
    required: Boolean(field.required),
    placeholder: field.placeholder ?? "",
    defaultValue: field.defaultValue ?? "",
    options: Array.isArray(field.options) ? field.options : [],
    validation: normalizeFieldValidation(field.validation),
    conditionalBehavior: normalizeConditionalBehavior(field.conditionalBehavior),
  }
  if (defaultValueOffset) {
    normalized.defaultValueOffset = defaultValueOffset
  }
  return normalized
}

export function normalizeFormFields(fields) {
  if (!Array.isArray(fields)) return []
  return fields.map((f) => normalizeFormField(f)).filter(Boolean)
}

/** Normalized default fields when no form is configured (post-completion fallback). */
export const FALLBACK_COMPLETION_FORM_FIELDS = normalizeFormFields(
  FALLBACK_COMPLETION_FORM_FIELDS_RAW,
)

function normalizeLabelKey(label) {
  return String(label ?? "")
    .replace(/<[^>]*>/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
}

/**
 * True when the user chose a "deferred" resolution on a select/radio above the defer-date field.
 * Used for post-completion forms that capture defer date only when resolution is deferred.
 *
 * @param {Record<string, unknown>} formData
 * @param {Array<{ id?: string, type?: string, label?: string, options?: Array<{ value?: unknown, label?: unknown }> }>} formFields
 * @param {string|number|null|undefined} deferDateFieldId
 */
export function resolutionIndicatesDeferred(formData, formFields, deferDateFieldId) {
  const did = deferDateFieldId != null ? String(deferDateFieldId).trim() : ""
  if (!did || !Array.isArray(formFields) || formFields.length === 0) {
    return false
  }
  const deferIdx = formFields.findIndex((f) => f?.id != null && String(f.id) === did)
  if (deferIdx < 0) {
    return false
  }

  let controller = formFields.find(
    (f) =>
      (f?.type === "select" || f?.type === "radio") &&
      normalizeLabelKey(f?.label) === normalizeLabelKey("Resolution"),
  )
  if (!controller) {
    controller = formFields.find(
      (f, i) => i < deferIdx && (f?.type === "select" || f?.type === "radio"),
    )
  }
  if (!controller?.id) {
    return false
  }

  const raw = formData[controller.id]
  if (raw === undefined || raw === null || raw === "") {
    return false
  }

  const asStr = String(raw).toLowerCase()
  if (asStr.includes("defer")) {
    return true
  }

  if (
    (controller.type === "select" || controller.type === "radio") &&
    Array.isArray(controller.options)
  ) {
    const opt = controller.options.find((o) => String(o.value) === String(raw))
    const label = String(opt?.label ?? "").toLowerCase()
    if (label.includes("defer")) {
      return true
    }
  }

  return false
}

/**
 * Missing required fields using caller-supplied visibility/required predicates.
 */
export function getMissingRequiredFieldsWith(
  fields,
  formData,
  isFieldVisibleFn,
  isFieldRequiredFn,
) {
  if (!Array.isArray(fields)) return []
  return fields.filter((field) => {
    if (!isFieldVisibleFn(field)) return false
    if (!isFieldRequiredFn(field)) return false
    const value = formData[field.id]
    if (field.type === "checkbox") return value !== true
    return value === undefined || value === null || value === ""
  })
}

export function buildVisibleFormPayloadWith(fields, formData, isFieldVisibleFn) {
  const payload = {}
  if (!Array.isArray(fields)) return payload
  for (const field of fields) {
    if (!isFieldVisibleFn(field)) continue
    payload[field.id] = formData[field.id]
  }
  return payload
}

/**
 * Map snapshot field ids → current template ids when the form was edited after the
 * instance was created (ids drift but order / labels still line up).
 */
export function buildSnapshotToTemplateFieldIdMap(tmplFields, snapFields) {
  const map = new Map()
  if (!Array.isArray(tmplFields) || !Array.isArray(snapFields)) return map

  const n = Math.min(tmplFields.length, snapFields.length)
  for (let i = 0; i < n; i++) {
    const t = tmplFields[i]
    const s = snapFields[i]
    if (t?.id == null || s?.id == null) continue
    if (String(t.id) !== String(s.id)) {
      map.set(String(s.id), String(t.id))
    }
  }

  for (const s of snapFields) {
    if (!s?.id) continue
    const sid = String(s.id)
    if (map.has(sid)) continue
    const labelKey = normalizeLabelKey(s.label)
    if (!labelKey) continue
    const t = tmplFields.find(
      (f) => normalizeLabelKey(f?.label) === labelKey && f?.id != null,
    )
    if (t && String(t.id) !== sid) {
      map.set(sid, String(t.id))
    }
  }

  return map
}

/**
 * Pair a template field with its snapshot row when ids drift (instance created before edits).
 * Order: exact id match → remap(snap.id)→template id → same index → same normalized label.
 */
function resolveSnapshotFieldForTemplateField(
  tmplField,
  index,
  snapFields,
  snapshotToTemplateIdMap,
) {
  if (!Array.isArray(snapFields) || !tmplField?.id) return null
  const tid = String(tmplField.id)

  const byExactId = snapFields.find(
    (s) => s?.id != null && String(s.id) === tid,
  )
  if (byExactId) return byExactId

  const byRemap = snapFields.find(
    (s) =>
      s?.id != null &&
      snapshotToTemplateIdMap.get(String(s.id)) === tid,
  )
  if (byRemap) return byRemap

  if (index >= 0 && index < snapFields.length) {
    const atIdx = snapFields[index]
    if (atIdx?.id != null) return atIdx
  }

  const labelKey = normalizeLabelKey(tmplField.label)
  if (labelKey) {
    const byLabel = snapFields.find(
      (s) => normalizeLabelKey(s?.label) === labelKey,
    )
    if (byLabel) return byLabel
  }

  return null
}

function remapConditionalSourceFieldId(raw, idMap) {
  if (!raw || typeof raw !== "object" || !idMap?.size) return raw
  const sid = String(raw.sourceFieldId ?? raw.source_field_id ?? "").trim()
  if (!sid || !idMap.has(sid)) return raw
  return {
    ...raw,
    sourceFieldId: idMap.get(sid),
  }
}

/**
 * Merge conditional rules from snapshot + template. Template wins on overlapping keys,
 * but fill empty `value` / `sourceFieldId` from snapshot when the template omits them.
 */
function mergeConditionalBehaviorObjects(templateRaw, snapshotRaw) {
  const t =
    templateRaw != null && typeof templateRaw === "object" ? templateRaw : null
  const s =
    snapshotRaw != null && typeof snapshotRaw === "object" ? snapshotRaw : null
  if (t && s) {
    const merged = { ...s, ...t }
    const tVal = t.value != null ? String(t.value).trim() : ""
    const sVal = s.value != null ? String(s.value).trim() : ""
    merged.value = tVal || sVal || ""
    const tSid = String(t.sourceFieldId ?? t.source_field_id ?? "").trim()
    const sSid = String(s.sourceFieldId ?? s.source_field_id ?? "").trim()
    merged.sourceFieldId = tSid || sSid || ""
    return merged
  }
  return t ?? s
}

/**
 * Merge live Form.fields (template) with FormInstance.form_data.fields (variable substitution).
 * @param {{ form_data?: { fields?: unknown[] }, form?: { fields?: unknown[] } }} formInstance
 */
export function mergeFormTemplateWithSnapshot(formInstance) {
  const snapFields = Array.isArray(formInstance.form_data?.fields)
    ? formInstance.form_data.fields
    : []
  const tmplFields = Array.isArray(formInstance.form?.fields)
    ? formInstance.form.fields
    : []
  const snapshotToTemplateIdMap = buildSnapshotToTemplateFieldIdMap(
    tmplFields,
    snapFields,
  )

  const mergeOne = (templateField, snap) => {
    const t = templateField || {}
    const s = snap || {}
    const mergedConditional = mergeConditionalBehaviorObjects(
      t.conditionalBehavior,
      s.conditionalBehavior,
    )
    const snapDefault =
      s.defaultValue != null && String(s.defaultValue).trim() !== ""
        ? s.defaultValue
        : null
    const merged = normalizeFormField({
      ...t,
      id: t.id,
      label: s.label ?? t.label ?? "",
      type: t.type ?? s.type ?? "text",
      required: t.required ?? s.required,
      placeholder: s.placeholder ?? t.placeholder ?? "",
      defaultValue: snapDefault ?? t.defaultValue ?? "",
      options: Array.isArray(s.options) ? s.options : t.options ?? [],
      validation: mergeFieldValidation(t.validation, s.validation),
      conditionalBehavior: remapConditionalSourceFieldId(
        mergedConditional,
        snapshotToTemplateIdMap,
      ),
    })
    // Prefer snapshot defaultValueOffset only when default was not yet resolved to ISO
    if (snapDefault && /^\d{4}-\d{2}-\d{2}$/.test(String(snapDefault).trim())) {
      delete merged.defaultValueOffset
    } else if (normalizeDateOffset(s.defaultValueOffset)) {
      merged.defaultValueOffset = normalizeDateOffset(s.defaultValueOffset)
    } else if (normalizeDateOffset(t.defaultValueOffset)) {
      merged.defaultValueOffset = normalizeDateOffset(t.defaultValueOffset)
    }
    return merged
  }

  if (tmplFields.length > 0) {
    return tmplFields
      .map((field, index) =>
        mergeOne(
          field,
          resolveSnapshotFieldForTemplateField(
            field,
            index,
            snapFields,
            snapshotToTemplateIdMap,
          ),
        ),
      )
      .filter(Boolean)
  }

  return snapFields.map((field) => mergeOne(field, field)).filter(Boolean)
}

export function defaultValuesFromFields(fields) {
  const initial = {}
  for (const f of fields) {
    if (f?.id != null && f.defaultValue) {
      initial[f.id] = f.defaultValue
    }
  }
  return initial
}

/** Fields that are visible, required, and still empty / unchecked. */
export function getMissingRequiredFields(fields, formData) {
  const { isFieldVisible, isFieldRequired } = formConditionEvaluators(
    formData,
    fields,
  )
  return fields.filter((field) => {
    if (!isFieldVisible(field)) return false
    if (!isFieldRequired(field)) return false
    const value = formData[field.id]
    if (field.type === "checkbox") return value !== true
    return value === undefined || value === null || value === ""
  })
}

/** Submit payload: only visible fields (hidden values omitted). */
export function buildVisibleFormPayload(fields, formData) {
  const { isFieldVisible } = formConditionEvaluators(formData, fields)
  const payload = {}
  for (const field of fields) {
    if (!isFieldVisible(field)) continue
    payload[field.id] = formData[field.id]
  }
  return payload
}

/**
 * Options for conditional "value to match" when the controlling field is select/radio/checkbox.
 * @param {Array<{ id?: string, type?: string, options?: Array<{ value?: unknown, label?: unknown }> }>} allFields
 * @param {string} sourceFieldId
 * @returns {{ value: string, label: string }[] | null}
 */
export function getConditionalMatchOptionsFromFields(allFields, sourceFieldId) {
  const sid = sourceFieldId != null ? String(sourceFieldId) : ""
  const sourceField = allFields?.find((f) => String(f.id) === sid)
  if (!sourceField) return null

  if (sourceField.type === "checkbox") {
    return [
      { value: "true", label: "Checked" },
      { value: "false", label: "Unchecked" },
    ]
  }

  if (
    (sourceField.type === "select" || sourceField.type === "radio") &&
    Array.isArray(sourceField.options) &&
    sourceField.options.length > 0
  ) {
    return sourceField.options.map((o) => ({
      value: o.value != null ? String(o.value) : "",
      label:
        (o.label != null ? String(o.label) : String(o.value ?? "")).trim() ||
        String(o.value ?? ""),
    }))
  }

  return null
}
